import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react';
import Text from 'components/Text';
import useTheme from 'hooks/useTheme';
import { IOption } from '../Select/SingleSelect';
import Select from 'react-select';
import { FieldProps, getIn } from 'formik';
import { ValueType } from 'react-select/lib/types';
import styled from 'styled-components';
import StyledErrorMessage from '../StyledErrorMessage';
import ClearSelect from '../ClearSelect';

interface IProps {
  hoursName: string;
  minutesName: string;
  onClear: () => void;
  // eslint-disable-next-line @typescript-eslint/ban-types
  minuteInputProps?: object;
  onlyHours?: boolean;
  step?: number;
  options: IOption[];
}

const Minutes: FunctionComponent<IProps & FieldProps> = ({
  minuteInputProps,
  onlyHours,
  hoursName,
  minutesName,
  field: { name, value, onBlur },
  form: { errors, touched, values, setFieldValue },
  onClear,
  options,
  step = 15,
}) => {
  const theme = useTheme();
  const minutesRef = useRef<Select<IOption>>(null);

  const hasError = useMemo(
    () => Boolean(getIn(errors, name) && getIn(touched, name)),
    [errors, touched, name]
  );

  const hasMinutesError = useMemo(
    () => Boolean(getIn(errors, minutesName)),
    [errors, touched, minutesName]
  );

  const onChangeMinutes = useCallback(
    (selected: ValueType<IOption>) => setFieldValue(minutesName, selected),
    [minutesName, setFieldValue]
  );

  useEffect(
    () => {
      onlyHours && onChangeMinutes(options.find(({ value: minuteValue }) => minuteValue === 0));
    },
    // Don't add "options" to deps.
    [onlyHours, setFieldValue, minutesName]
  );

  //set initial minutes
  useEffect(() => {
    if (!value || values[minutesName]) return;
    const [_, minutes] = value.split(':');
    if (minutes && getIn(values, [minutesName, 'value']) !== Number(minutes)) {
      const tempMinute = Math.floor(minutes / step) * step;
      const selectedMinute = options.find((o) => o.value === tempMinute);
      selectedMinute && onChangeMinutes(selectedMinute);
    }
  }, [options, value, values[minutesName], onChangeMinutes]);

  const clear = useCallback(() => {
    if (minutesRef.current !== null) {
      minutesRef.current.onChange(undefined, { action: 'clear' });
    }
    onChangeMinutes(undefined);
    setFieldValue(name, '');
  }, [minutesRef.current, onChangeMinutes, setFieldValue, name]);

  // clear minutes field if current isn't exist in list of minutes
  useEffect(() => {
    const minutes = getIn(values, [minutesName, 'value']);
    if (!onlyHours && minutes !== undefined && !options.find((item) => item.value === minutes)) {
      clear();
    }
  }, [options, values[minutesName], clear, onlyHours]);

  // clear minutes field if hours is empty
  useEffect(() => {
    const minutes = getIn(values, [minutesName, 'value']);
    if (!onlyHours && !Boolean(values[hoursName]) && minutes) {
      clear();
    }
  }, [values[minutesName], values[hoursName], clear, onlyHours]);

  return (
    <>
      <Text className="hidden-xs mt14" color={theme.colors.dark}>
        :
      </Text>
      <div>
        <StyledMinuteSelect
          ref={minutesRef}
          name={minutesName}
          options={options}
          error={hasError || hasMinutesError}
          onChange={onChangeMinutes}
          onBlur={onBlur}
          value={values[minutesName]}
          placeholder="Minutes"
          onClear={onClear}
          {...minuteInputProps}
          isClearable={false} // must be false always
        />
        {hasMinutesError && <StyledErrorMessage>{errors[minutesName]}</StyledErrorMessage>}
      </div>
    </>
  );
};

export default Minutes;

const StyledMinuteSelect = styled(ClearSelect)`
  ${(props) => props.theme.media.xs`
    margin-top: 8px;
  `}
`;
